import React, { useState, useEffect } from 'react';
import { FaEdit, FaTrashAlt, FaPlus, FaTimes } from 'react-icons/fa';
import Button from '../../../DynamicComp/Button';
import GenericTable from '../../../DynamicComp/Table';
import Input from '../../../DynamicComp/Input';
import Selector from '../../../DynamicComp/Selector';
import TextBox from '../../../DynamicComp/TextBox';
import ProductGroupImg from './ProductGroupImg';
import axiosClient, { deleteData, getData, postData, putData } from '../../../../global/Globalapi';
import { showAlert } from '../../../DynamicComp/Alert';

const ProductGroup = () => {
  // --- STATE MANAGEMENT ---
  const baseURL = axiosClient.defaults.baseURL;
  const [formData, setFormData] = useState({ 
    category_name: '', 
    parentName: '', 
    description: '', 
    entryType: 'category' 
  });

  const [product_categorydata, setData] = useState([]);
  const [categoryList, setCategoryList] = useState([]);
  const [editId, setEditId] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [images, setImages] = useState([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [activeTab, setActiveTab] = useState('category');

  // --- DATA FETCHING ---
  const fetchProductGroupsForTable = async (type = 'category') => {
    try {
      const response = await getData(`/product_category?type=${type}`);
      setData(response.data);
    } catch (error) { 
      console.error('Error fetching product groups:', error); 
    }
  };

  const fetchCategoriesForSelector = async () => {
    try {
      const response = await getData('/product_category?type=category');
      setCategoryList(response.data);
    } catch (error) { 
      console.error('Error fetching categories for selector:', error); 
    }
  };

  useEffect(() => {
    fetchCategoriesForSelector();
  }, []);

  useEffect(() => {
    fetchProductGroupsForTable(activeTab);
  }, [activeTab]);

  // --- FORM HANDLERS ---
  const handleCancel = () => {
    setFormData({ 
      category_name: '', 
      parentName: '', 
      description: '', 
      entryType: 'category' 
    });
    setImages([]);
    setEditId(null);
    setShowForm(false);
    setIsSubmitting(false);
  };

  const handleChange = (e) => setFormData({ ...formData, [e.target.name]: e.target.value });
  const handleImagesUpdate = (updatedImages) => setImages(updatedImages);

  // --- CRUD OPERATIONS ---
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.category_name.trim()) {
      showAlert({
        type: 'info',
        message: 'Category name is required',
        title: 'Info',
      });
      return;
    }
    
    setIsSubmitting(true);

    try {
      const formDataToSend = new FormData();
      formDataToSend.append('category_name', formData.category_name.trim());
      formDataToSend.append('parentName', formData.parentName || '');
      formDataToSend.append('description', formData.description || '');
      formDataToSend.append('entryType', formData.entryType);
      
      const imagesData = images.map((image, index) => ({ 
        id: image.id || null, 
        image_file_name: image.image_file_name || (image.file ? image.file.name : `image_${index}`), 
        displayOrder: image.displayOrder || index + 1, 
        description: image.description || '', 
        isNew: editId ? !image.id : true, 
        isDeleted: image.isDeleted || false 
      }));

      formDataToSend.append('images_data', JSON.stringify(imagesData));
      images.forEach((image) => { 
        if (image.file instanceof File) { 
          formDataToSend.append('image_files', image.file); 
        } 
      });
      
      const response = editId
        ? await putData(`/product_categoryupdate/${editId}`, formDataToSend, { 
            headers: { 'Content-Type': 'multipart/form-data' } 
          })
        : await postData('/product_categoryadd', formDataToSend, { 
            headers: { 'Content-Type': 'multipart/form-data' } 
          });
          
      showAlert({
        type: 'success',
        message: response.data.message,
        title: 'Success',
      });

      if (!editId && response.data.newGroup?.product_category_id) {
        setEditId(response.data.newGroup.product_category_id);
      }

      await fetchProductGroupsForTable(activeTab);
      await fetchCategoriesForSelector();
      handleCancel();
      
    } catch (error) {
      console.error('Submission error:', error);
      showAlert({
        type: 'error',
        message: error.response?.data?.message || 'A network error occurred.',
        title: 'Error',
      });
    } finally {
      setIsSubmitting(false);
    }
  };

const handleEdit = async (group) => {
  handleCancel();
  
  setFormData({ 
    category_name: group.category_name,
    parentName: group.parent_product_category_id || '',
    description: group.category_description || ''
  });
  setEditId(group.product_category_id); 

  setShowForm(true);
  
  // Fetch images with CORRECTED path (singular "image" not plural "images")
  try {
    const response = await getData(`/product_category_images/${group.product_category_id}`);
    setImages(response.data.map(img => ({ 
      id: img.id, 
      image_file_name: img.fileName, 
      displayOrder: img.displayOrder, 
      description: img.description, 
      // FIXED: Changed from 'images' to 'image' (singular)
      file: `${baseURL}/uploads/product_category_image/${img.fileName}`, 
      isUpdated: false 
    })));
  } catch (err) { 
    console.error('Failed to fetch images:', err);
    setImages([]);
  }
};

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this category?')) return;

    try {
      const response = await deleteData(`/product_category/${id}`); 
      showAlert({
        type: 'success',
        message: response.data.message,
        title: 'Success',
      });
      await fetchProductGroupsForTable(activeTab);
    } catch (error) { 
      showAlert({
        type: 'error',
        message: error.response?.data?.message || "Failed to delete.",
        title: 'Error',
      });
    }
  };

  // --- TABLE CONFIGURATION ---
  const columns = [
  { key: "sno", label: "S.No", align: "center", width: "80px" },
  { key: "parent_category_name", label: "Parent Group" }, // ✅ FIXED: Changed from "parentname"
  { key: "category_name", label: "Name", sortable: true },
  { key: "category_description", label: "Description" },
  { key: "actions", label: "Actions", align: "center", width: "120px" },
];

const customRenderers = {
  // ✅ ADDED: Renderer for parent_category_name to handle null values
  parent_category_name: (row) => (
    <span>{row.parent_category_name || '-'}</span>
  ),
  actions: (row) => (
    <div className="flex justify-center items-center gap-2">
      <button 
        onClick={() => handleEdit(row)} 
        className="p-1.5 bg-blue-500 text-white rounded hover:bg-blue-600" 
        title="Edit"
      >
        <FaEdit />
      </button>
      <button 
        onClick={() => handleDelete(row.product_category_id)} 
        className="p-1.5 bg-red-600 text-white rounded hover:bg-red-700" 
        title="Delete"
      >
        <FaTrashAlt />
      </button>
    </div>
  ),
};

  return (
    <div className="flex flex-col items-center min-h-screen w-full p-4 sm:p-8 bg-gray-50">
      
      {/* PRODUCT CATEGORY LIST */}
      <div className="bg-white p-6 rounded-xl shadow-lg w-full overflow-x-auto mb-8">
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-2xl font-bold text-gray-800">Product Categories </h2>
          <Button 
            variant="danger" 
            onClick={() => {
              setFormData({ 
                category_name: '', 
                parentName: '', 
                description: '', 
                entryType: 'category' 
              });
              setImages([]);
              setEditId(null);
              setShowForm(true);
            }} 
            className="p-2 rounded-md flex items-center space-x-2 text-sm"
          >
            <FaPlus /><span>Add Category</span>
          </Button>
        </div>
        
        <div className="border-b border-gray-200">
          <nav className="-mb-px flex space-x-6">
            <button 
              onClick={() => setActiveTab('category')} 
              className={`whitespace-nowrap py-3 px-1 border-b-2 font-medium text-sm ${
                activeTab === 'category' 
                  ? 'border-red-600 text-red-600' 
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              Categories
            </button>
          </nav>
        </div>
        
        <div className="mt-4">
          <GenericTable 
            headerClassName="bg-red-700 text-white"
            columns={columns} 
            data={product_categorydata} 
            customRenderers={customRenderers} 
            rowKey="product_category_id" 
          />
        </div>
      </div>

      {/* ADD/EDIT FORM */}
      {showForm && (
        <div className="bg-white p-6 rounded-xl shadow-lg w-full mt-8">
          <form onSubmit={handleSubmit} noValidate>
            <h2 className="text-2xl font-bold text-left mb-6 text-gray-800">
              {editId ? 'Edit Product Category' : 'Add Product Category'}
            </h2>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
              <Selector 
                label="Parent Category" 
                options={categoryList
                  .filter(g => !editId || g.product_category_id !== editId)
                  .map(g => ({ 
                    value: g.product_category_id, 
                    label: g.category_name 
                  }))} 
                value={formData.parentName} 
                onChange={(v) => setFormData({ ...formData, parentName: v })} 
                placeholder="Select a Parent Category" 
              />
              <Input 
                type="text" 
                label="Category Name *" 
                name="category_name" 
                value={formData.category_name} 
                onChange={handleChange} 
                required 
              />
            </div>

            <TextBox 
              label="Description" 
              name="description" 
              value={formData.description} 
              onChange={handleChange} 
              rows={4} 
            />

            <div className="my-8">
              <ProductGroupImg
                onImagesUpdate={handleImagesUpdate}
                initialImages={images}
                imageBasePath="product_category_image"
                deletePath="/product_category_images" 
              />
            </div>

            <div className="flex justify-end space-x-4 mt-8 border-t pt-6">
              <Button 
                variant="danger" 
                type="submit" 
                className="px-8 py-2 rounded-lg" 
                disabled={isSubmitting}
              >
                {isSubmitting ? 'Saving...' : (editId ? 'Update' : 'Save')}
              </Button>
              <Button 
                type="button" 
                variant="danger" 
                onClick={handleCancel} 
                className="px-8 py-2 rounded-lg"
              >
                Cancel
              </Button>
            </div>
          </form>
        </div>
      )}
    </div>
  );
};

export default ProductGroup;